import { zodResolver } from '@hookform/resolvers/zod';
import CT from '@marketplace-ai/tags';
import { ReactNode } from 'react';
import { FormProvider, Path, SubmitErrorHandler } from 'react-hook-form';
import { SingleValue } from 'react-select';
import {
  AsyncCreatableSelect,
  DatePicker,
  FormLayout,
  Select,
  TextInput,
  Toggle,
} from '~/components';
import { Wizard } from '~/components/Wizard/Wizard';
import { WizardStepsProps } from '~/components/Wizard/types';

import {
  loadMedicalBoardOptions,
  loadSpecBoardOptions,
  loadSpecCertOptionsBySpecialty,
  loadSpecialtyOptions,
  loadSubSpecCertOptions,
} from '~/api/utils';
import { useCustomForm } from '~/hooks/useCustomForm';
import { useServerWarnings } from '~/hooks/useServerWarnings';
import { useWizardSteps } from '~/hooks/useWizardSteps';
import { FormRow as Row } from '../../../components/forms/FormRow';
import { FormSection as Section } from '../../../components/forms/FormSection';
import { AsyncSelectOrInput } from './components/AsyncSelectOrInput';
import { ACTIVE_STATUSES, STATUS_OPTIONS, TYPE_OPTIONS } from './constants';
import { boardCertSchema } from './schemas';
import { BoardCertFormValues } from './types';

interface FormProps {
  form: Partial<BoardCertFormValues>;
  handleClose: () => void;
  handleCreate: (values: BoardCertFormValues) => void;
  handleEdit: (values: BoardCertFormValues) => void;
  isEdit: boolean;
  providerCategoryId: number;
}
const TAGS = CT.boardCerts.form;

const VALID_FIELDS: (keyof BoardCertFormValues)[] = ['status', 'certType'];
const STEP_FIELDS: Path<BoardCertFormValues>[][] = [
  [
    'certType',
    'medicalBoard',
    'specialtyBoard',
    '_specialtyBoardName',
    'specialty',
    '_specialtyName',
    'specialtyCertificate',
    '_specialtyCertName',
    'subSpecialtyCertificate',
    '_subSpecialtyName',
  ],
  ['status', 'certNum', 'mocReqFlag'],
  ['origCertDate', 'expiryDate', 'recertDate'],
];

const EMPTY_CERT_OPTION = { label: '', value: '', id: undefined };

export const BoardCertForm = (props: FormProps) => {
  const { handleClose, providerCategoryId, form, isEdit, handleCreate, handleEdit } = props;
  const methods = useCustomForm<BoardCertFormValues>({
    resolver: zodResolver(boardCertSchema),
    defaultValues: form,
  });
  useServerWarnings(methods, form.warnings ?? undefined, VALID_FIELDS);

  const { handleSubmit, watch, setValue } = methods;

  const onSubmitSuccess = (values: BoardCertFormValues) => {
    if (isEdit) {
      handleEdit(values);
    } else {
      handleCreate(values);
    }
  };

  const onSubmitError: SubmitErrorHandler<BoardCertFormValues> = (error) => {
    console.log('Error');
    console.log(error);
  };

  /**
   * Clears both certificate fields and their free-text fallbacks.
   *
   * [MCV-1627] Each cascade handler cleared only the select half of its
   * descendants. That was inert while the schema and payload read nothing but
   * select values, but both now accept the AsyncSelectOrInput free-text
   * fallback, so a `_`-prefixed field left behind by a changed ancestor is a
   * live value: it satisfies the required-certificate check for a field the
   * user can see is empty, and ships to the server. Clear both halves at every
   * level, cascading downward.
   */
  const clearCertificateFields = () => {
    // Cleared to an empty option rather than `undefined`: react-hook-form's
    // Controller falls back to the form's `defaultValues` whenever the current
    // value is undefined, so a field that unmounts and remounts -- which is
    // exactly what a certification-type switch does to these two -- came back
    // holding the value it was loaded with. An empty option is defined, so it
    // survives the remount, and `!value` still reads as empty everywhere. Only
    // the certificate leaves are cleared this way; the boards and specialty
    // above them are parents, and AsyncSelectOrInput derives `disabled` from
    // its parent being `undefined`, so giving them an empty object would
    // silently enable a child whose parent is unset.
    setValue('specialtyCertificate', EMPTY_CERT_OPTION);
    setValue('subSpecialtyCertificate', EMPTY_CERT_OPTION);
    setValue('_specialtyCertName', '');
    setValue('_subSpecialtyName', '');
  };

  /**
   * Clears the specialty and everything below it.
   *
   * [MCV-1627] `null` rather than `undefined`, for the same Controller
   * `defaultValues` fallback described above: these components are keyed by
   * their ancestor's id, so changing the ancestor remounts them, and a
   * descendant cleared to `undefined` came back holding the value the record
   * loaded with. `null` is a defined value, so it survives the remount.
   *
   * It cannot be the empty option used for the certificates, because these two
   * are parents: AsyncSelectOrInput reads `disabled` off its parent field, and
   * an empty object is truthy enough to enable a child whose parent is unset.
   * `null` satisfies both -- that check is a loose `== null`, so it still reads
   * as absent, and the schema marks both fields `.nullish()`.
   */
  const clearSpecialtyFields = () => {
    setValue('specialty', null);
    setValue('_specialtyName', '');
    clearCertificateFields();
  };

  /** Clears the specialty board and everything below it. See above for `null`. */
  const clearSpecialtyBoardFields = () => {
    setValue('specialtyBoard', null);
    setValue('_specialtyBoardName', '');
    clearSpecialtyFields();
  };

  const certTypeVal = watch('certType').value;
  const statusVal = watch('status').label.toLowerCase();
  const medicalBoardId = watch('medicalBoard')?.id;
  const specialtyBoardId = watch('specialtyBoard')?.id;
  const specialtyId = watch('specialty')?.id;
  const { currentStep, goBack, goNext, navigationInProgress } = useWizardSteps({
    methods,
    stepFields: STEP_FIELDS,
  });

  const wizardSteps: WizardStepsProps[] = [
    {
      header: 'Type & Board',
      label: 'Type',
      cyTag: `${TAGS.form}-type-step`,
      footer: {
        secondaryButton: {
          label: 'Cancel',
          cyTag: TAGS.cancelBtn,
          onClick: handleClose,
        },
        primaryButton: {
          label: 'Next',
          cyTag: `${TAGS.submitBtn}-next-type`,
          disabled: navigationInProgress,
          onClick: goNext,
        },
      },
      children: ({ footer }: { footer: ReactNode }) => (
        <>
          <Section componentsPerLine={1}>
            <Row>
              <Select
                label="Type of Certification"
                options={TYPE_OPTIONS}
                name={'certType'}
                cyTag={TAGS.certType}
                required
                // [MCV-1627] Switching type swaps which certificate field is
                // rendered, and the one that unmounts keeps its value. Without
                // this, a SUBSPECIALTY record edited into a SPECIALTY one still
                // carried its old subspecialty certificate, which outranks the
                // newly entered specialty certificate when the payload is
                // built. The boards above stay applicable, so only the
                // certificate level is cleared.
                onChange={clearCertificateFields}
              />
            </Row>
          </Section>
          <Section componentsPerLine={1}>
            <Row>
              <AsyncCreatableSelect
                name={'medicalBoard'}
                label={'Medical Board'}
                cyTag={TAGS.medicalBoard}
                required
                minChars={false}
                onChange={(option: SingleValue<{ value: string; label: string; id?: number }>) => {
                  if (option) {
                    clearSpecialtyBoardFields();
                  }
                }}
                loadOptions={() => loadMedicalBoardOptions(providerCategoryId, providerCategoryId)}
              />
            </Row>
            <Row>
              <AsyncSelectOrInput
                key={medicalBoardId ?? 'no-medical-board'}
                label="Specialty Board"
                selectName={'specialtyBoard'}
                textInputName={'_specialtyBoardName'}
                selectCyTag={TAGS.specialtyBoard}
                inputCyTag={TAGS.customSpecialtyBoard}
                parentId={medicalBoardId}
                parentName="medicalBoard"
                required
                onChange={(option: SingleValue<{ value: string; label: string; id?: number }>) => {
                  if (option) {
                    clearSpecialtyFields();
                  }
                }}
                loadOptions={loadSpecBoardOptions}
              />
            </Row>
            {certTypeVal == 'SPECIALTY' && (
              <>
                <Row>
                  <AsyncSelectOrInput
                    key={specialtyBoardId ?? 'no-specialty-board'}
                    label="Specialty"
                    selectName={'specialty'}
                    textInputName={'_specialtyName'}
                    selectCyTag={TAGS.specialty}
                    inputCyTag={TAGS.customSpecialty}
                    parentId={specialtyBoardId}
                    parentName="specialtyBoard"
                    required
                    onChange={(
                      option: SingleValue<{ value: string; label: string; id?: number }>
                    ) => {
                      if (option) {
                        clearCertificateFields();
                      }
                    }}
                    loadOptions={loadSpecialtyOptions}
                  />
                </Row>
                <Row>
                  <AsyncSelectOrInput
                    key={specialtyId ?? 'no-specialty'}
                    label="Specialty Certificate"
                    selectName={'specialtyCertificate'}
                    textInputName={'_specialtyCertName'}
                    selectCyTag={TAGS.specialtyCert}
                    inputCyTag={TAGS.customSpecialtyCert}
                    parentId={specialtyId}
                    parentName="specialty"
                    required
                    onChange={(
                      option: SingleValue<{ value: string; label: string; id?: number }>
                    ) => {
                      if (option) {
                        // Empty option rather than `undefined`, for the
                        // remount reason described on clearCertificateFields.
                        setValue('subSpecialtyCertificate', EMPTY_CERT_OPTION);
                      }
                    }}
                    loadOptions={loadSpecCertOptionsBySpecialty}
                  />
                </Row>
              </>
            )}
            {certTypeVal == 'SUBSPECIALTY' && (
              <Row>
                <AsyncSelectOrInput
                  key={specialtyBoardId ?? 'no-specialty-board'}
                  label="Subspecialty Certificate"
                  selectName={'subSpecialtyCertificate'}
                  textInputName={'_subSpecialtyName'}
                  selectCyTag={TAGS.subSpecialtyCert}
                  inputCyTag={TAGS.customSubSpecialtyCert}
                  parentId={specialtyBoardId}
                  parentName="specialtyBoard"
                  required
                  onChange={() => {
                    // No additional clearing needed for subspecialty.
                  }}
                  loadOptions={loadSubSpecCertOptions}
                />
              </Row>
            )}
          </Section>
          {footer}
        </>
      ),
    },
    {
      header: 'Status & Identifiers',
      label: 'Status',
      cyTag: `${TAGS.form}-status-step`,
      footer: {
        backButton: {
          label: 'Back',
          cyTag: `${TAGS.submitBtn}-back-status`,
          onClick: goBack,
        },
        secondaryButton: {
          label: 'Cancel',
          cyTag: TAGS.cancelBtn,
          onClick: handleClose,
        },
        primaryButton: {
          label: 'Next',
          cyTag: `${TAGS.submitBtn}-next-status`,
          disabled: navigationInProgress,
          onClick: goNext,
        },
      },
      children: ({ footer }: { footer: ReactNode }) => (
        <>
          <Section componentsPerLine={2}>
            <Row>
              <TextInput name="certNum" cyTag={TAGS.certNum} label={'Certificate Number'} />
            </Row>
          </Section>
          <Section componentsPerLine={1}>
            <Row>
              <Select
                options={STATUS_OPTIONS}
                name={'status'}
                cyTag={TAGS.status}
                label={'Status'}
                required
              />
            </Row>
          </Section>
          <Section componentsPerLine={1}>
            <Row>
              <Toggle label="Does this certificate require maintenance?" name={'mocReqFlag'} />
            </Row>
          </Section>
          {footer}
        </>
      ),
    },
    {
      header: 'Dates',
      label: 'Dates',
      cyTag: `${TAGS.form}-dates-step`,
      footer: {
        backButton: {
          label: 'Back',
          cyTag: `${TAGS.submitBtn}-back-dates`,
          onClick: goBack,
        },
        secondaryButton: {
          label: 'Cancel',
          cyTag: TAGS.cancelBtn,
          onClick: handleClose,
        },
        primaryButton: {
          label: 'Save',
          cyTag: TAGS.submitBtn,
          isSubmit: true,
        },
      },
      children: ({ footer }: { footer: ReactNode }) => (
        <>
          <Section componentsPerLine={1}>
            <Row>
              <DatePicker
                name={'origCertDate'}
                label={'Original Certified Date'}
                cyTag={TAGS.origCertDate}
                required
              />
            </Row>
            {!ACTIVE_STATUSES.map((status) => status.toLowerCase()).includes(statusVal) && (
              <Row>
                <DatePicker name={'expiryDate'} label={'Expired Date'} cyTag={TAGS.expiryDate} />
              </Row>
            )}
            <Row>
              <DatePicker name={'recertDate'} label={'Recertified Date'} cyTag={TAGS.recertDate} />
            </Row>
          </Section>
          {footer}
        </>
      ),
    },
  ];

  return (
    <FormProvider {...methods}>
      <form
        data-cy={TAGS.form}
        onSubmit={handleSubmit(onSubmitSuccess, onSubmitError)}
        id="board-cert-form"
      >
        <FormLayout className="bg-white">
          <Wizard steps={wizardSteps} currentStep={currentStep} />
        </FormLayout>
      </form>
    </FormProvider>
  );
};
